// P3-017: Main Phaser battle scene
// Renders 7x5 tile grid, handles player input, animates swap/match/gravity/cascade
// Communicates with React UI via EventBus

import Phaser from 'phaser';
import { ElementType } from '@/shared/types/common';
import { BOARD_COLS, BOARD_ROWS, TILE_SIZE } from '../data/board-constants';
import { TileSprite } from '../entities/TileSprite';
import { EnemySprite } from '../entities/EnemySprite';
import { spawnDamageNumber } from '../entities/DamageNumber';
import { spawnMatchBurst, spawnComboFlash, spawnImpactBurst, spawnMatchGlowRing } from '../effects/ParticleEffects';
import { spawnProjectile } from '../entities/TileProjectile';
import { playOverloadGlitch, playSkillFlash } from '../effects/ScreenEffects';
import { EventBus } from '@/shared/game/EventBus';
import { GAME_DPR, GAME_WIDTH, GAME_HEIGHT } from '@/shared/game/main';
import { useBoardStore } from '../stores/boardStore';
import { useBattleStore } from '../stores/battleStore';
import * as BattleOrchestrator from '../systems/BattleOrchestrator';
import { areAdjacent } from '../systems/BoardSystem';
import { getComboMultiplier } from '../systems/DamageCalculator';
import type { Tile, TilePosition, CascadeStep } from '../types/board';
import type { BattleTurnResult } from '../types/battle-internal';

export const BATTLE_SCENE_KEY = 'BattleScene';

/** Pixel offsets for centering the grid in the scene */
const GRID_PADDING_X = 16;
/** Board bottom sits 100px from canvas bottom: 600 - 100 - (5*64) = 180 */
const GRID_PADDING_Y = GAME_HEIGHT - 100 - BOARD_ROWS * TILE_SIZE;

/** Border styling for the board frame */
const BORDER_COLOR = 0x00e5ff;     // Cyan glow matching the tile bg
const BORDER_ALPHA = 0.85;
const BORDER_WIDTH = 3;
const BORDER_PADDING = 6;          // Gap between grid edge and border
const BORDER_CORNER_RADIUS = 8;

/** Cascade animation timing constants (ms) — [TUNING] marks values for playtesting */
const HIGHLIGHT_DURATION_FIRST = 150;   // [TUNING] Match highlight for round 1 (player just swapped)
const HIGHLIGHT_DURATION_CASCADE = 200; // [TUNING] Match highlight for round 2+ (surprise cascade)
const IMPACT_SETTLE_MS = 80;            // Brief pause after hit before gravity
const GRAVITY_SETTLE_MS = 40;           // Micro-pause after gravity settles
const INTER_ROUND_PAUSE_MS = 220;      // [TUNING] Dead air between cascade rounds
const PROJECTILE_STAGGER_MS = 25;      // Delay between each projectile in a salvo

export class BattleScene extends Phaser.Scene {
  /** Currently rendered TileSprite objects, indexed as [col][row] */
  private tileSprites: (TileSprite | null)[][] = [];

  /** Currently selected tile position (first click) */
  private selectedPos: TilePosition | null = null;

  /** Whether the scene is currently animating (lock input) */
  private animating = false;

  /** Enemy sprite reference */
  private enemySprite: EnemySprite | null = null;

  /** Board grid origin (top-left pixel position of the grid) */
  private gridOriginX = 0;
  private gridOriginY = 0;

  constructor() {
    super({ key: BATTLE_SCENE_KEY });
  }

  create(): void {
    // HiDPI: camera zoom maps the high-res canvas back to logical 800x600 coordinates
    this.cameras.main.setZoom(GAME_DPR);
    this.cameras.main.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);

    // Background image
    const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg-battle');
    bg.setDisplaySize(GAME_WIDTH, GAME_HEIGHT);
    bg.setDepth(-1);

    this.calculateGridOrigin();
    this.drawBoardBorder();
    this.buildBoard();
    this.buildEnemySprite();
    this.registerEventListeners();
  }

  shutdown(): void {
    this.cleanupEventListeners();
    this.tileSprites = [];
    this.selectedPos = null;
    this.animating = false;
    this.enemySprite = null;
  }

  // ──────────────────────────────────────────────────────
  // Grid layout
  // ──────────────────────────────────────────────────────

  private calculateGridOrigin(): void {
    const gridWidth = BOARD_COLS * TILE_SIZE;
    this.gridOriginX = Math.round((GAME_WIDTH - gridWidth) / 2) + GRID_PADDING_X / 2;
    this.gridOriginY = GRID_PADDING_Y;
  }

  /** Draw a cyan glowing border around the board */
  private drawBoardBorder(): void {
    const gridWidth = BOARD_COLS * TILE_SIZE;
    const gridHeight = BOARD_ROWS * TILE_SIZE;
    const x = this.gridOriginX - BORDER_PADDING;
    const y = this.gridOriginY - BORDER_PADDING;
    const w = gridWidth + BORDER_PADDING * 2;
    const h = gridHeight + BORDER_PADDING * 2;

    const gfx = this.add.graphics();
    gfx.setDepth(0);

    // Outer glow (wider, softer)
    gfx.lineStyle(BORDER_WIDTH + 4, BORDER_COLOR, BORDER_ALPHA * 0.25);
    gfx.strokeRoundedRect(x - 2, y - 2, w + 4, h + 4, BORDER_CORNER_RADIUS + 2);

    // Main border
    gfx.lineStyle(BORDER_WIDTH, BORDER_COLOR, BORDER_ALPHA);
    gfx.strokeRoundedRect(x, y, w, h, BORDER_CORNER_RADIUS);

    // Inner subtle line
    gfx.lineStyle(1, BORDER_COLOR, BORDER_ALPHA * 0.4);
    gfx.strokeRoundedRect(x + 3, y + 3, w - 6, h - 6, BORDER_CORNER_RADIUS - 2);
  }

  /** Convert board position to world pixel position */
  private boardToWorld(tileX: number, tileY: number): { x: number; y: number } {
    return {
      x: this.gridOriginX + tileX * TILE_SIZE + TILE_SIZE / 2,
      y: this.gridOriginY + tileY * TILE_SIZE + TILE_SIZE / 2,
    };
  }

  // ──────────────────────────────────────────────────────
  // Board construction
  // ──────────────────────────────────────────────────────

  private buildBoard(): void {
    const { tiles } = useBoardStore.getState();

    // Initialize empty grid
    this.tileSprites = [];
    for (let x = 0; x < BOARD_COLS; x++) {
      this.tileSprites[x] = [];
      for (let y = 0; y < BOARD_ROWS; y++) {
        this.tileSprites[x]![y] = null;
      }
    }

    // Create tile sprites from board state
    for (let x = 0; x < BOARD_COLS; x++) {
      for (let y = 0; y < BOARD_ROWS; y++) {
        const tile = tiles[x]?.[y];
        if (tile) {
          const pos = this.boardToWorld(x, y);
          const sprite = new TileSprite(this, pos.x, pos.y, tile.element, x, y);
          sprite.on('pointerdown', () => this.onTileClick(x, y));
          if (tile.locked) {
            sprite.setLocked(true);
          }
          this.tileSprites[x]![y] = sprite;
        }
      }
    }
  }

  private buildEnemySprite(): void {
    const battleState = useBattleStore.getState();
    const enemy = battleState.enemy;
    if (!enemy) return;

    // Position enemy above the grid
    const enemyX = GAME_WIDTH / 2;
    const enemyY = 55;

    const projectName = battleState.projectName || 'PROJECT';

    this.enemySprite = new EnemySprite(this, enemyX, enemyY, projectName, enemy.maxHp);
  }

  // ──────────────────────────────────────────────────────
  // Input handling
  // ──────────────────────────────────────────────────────

  private onTileClick(tileX: number, tileY: number): void {
    if (this.animating) return;

    const battleState = useBattleStore.getState();
    if (battleState.result !== null) return;

    // Check locked
    const boardTile = useBoardStore.getState().tiles[tileX]?.[tileY];
    if (boardTile?.locked) return;

    if (this.selectedPos === null) {
      // First selection
      this.selectedPos = { x: tileX, y: tileY };
      const sprite = this.tileSprites[tileX]?.[tileY];
      sprite?.setSelected(true);
    } else if (this.selectedPos.x === tileX && this.selectedPos.y === tileY) {
      // Deselect
      const sprite = this.tileSprites[tileX]?.[tileY];
      sprite?.setSelected(false);
      this.selectedPos = null;
    } else {
      // Second selection
      const from = this.selectedPos;
      const to = { x: tileX, y: tileY };

      if (areAdjacent(from, to)) {
        // 相邻 → 尝试交换
        this.selectedPos = null;
        const fromSprite = this.tileSprites[from.x]?.[from.y];
        fromSprite?.setSelected(false);
        this.executeSwap(from, to);
      } else {
        // 不相邻 → 取消旧选中，新 tile 成为选中
        const fromSprite = this.tileSprites[from.x]?.[from.y];
        fromSprite?.setSelected(false);
        this.selectedPos = to;
        const toSprite = this.tileSprites[tileX]?.[tileY];
        toSprite?.setSelected(true);
      }
    }
  }

  // ──────────────────────────────────────────────────────
  // Swap execution with animations
  // ──────────────────────────────────────────────────────

  private async executeSwap(from: TilePosition, to: TilePosition): Promise<void> {
    this.animating = true;

    const fromSprite = this.tileSprites[from.x]?.[from.y];
    const toSprite = this.tileSprites[to.x]?.[to.y];

    if (!fromSprite || !toSprite) {
      this.animating = false;
      return;
    }

    // Visual swap animation
    const fromWorld = this.boardToWorld(from.x, from.y);
    const toWorld = this.boardToWorld(to.x, to.y);

    await Promise.all([
      fromSprite.animateSwap(toWorld.x, toWorld.y),
      toSprite.animateSwap(fromWorld.x, fromWorld.y),
    ]);

    // Execute game logic through the orchestrator
    const turnResult = BattleOrchestrator.onPlayerSwap(from, to);

    if (!turnResult) {
      // Invalid swap — animate back
      await Promise.all([
        fromSprite.animateSwap(fromWorld.x, fromWorld.y),
        toSprite.animateSwap(toWorld.x, toWorld.y),
      ]);
      this.animating = false;
      return;
    }

    // Update sprite grid references for the swap
    this.tileSprites[from.x]![from.y] = toSprite;
    this.tileSprites[to.x]![to.y] = fromSprite;
    fromSprite.tileX = to.x;
    fromSprite.tileY = to.y;
    toSprite.tileX = from.x;
    toSprite.tileY = from.y;

    // Animate cascade steps
    await this.animateCascade(turnResult);

    // Sync any skill-induced board changes (e.g. BOARD_CHANGE)
    this.syncBoardVisual();

    // Update enemy sprite
    this.updateEnemyVisual(turnResult);

    this.animating = false;
  }

  // ──────────────────────────────────────────────────────
  // Cascade animation
  // ──────────────────────────────────────────────────────

  private async animateCascade(turnResult: BattleTurnResult): Promise<void> {
    for (let stepIdx = 0; stepIdx < turnResult.cascadeSteps.length; stepIdx++) {
      const step = turnResult.cascadeSteps[stepIdx]!;
      const chainDepth = stepIdx + 1;
      const roundWeight = step.removedPositions.length;
      const isFirstRound = stepIdx === 0;
      const isLastRound = stepIdx === turnResult.cascadeSteps.length - 1;

      // PHASE 1: Highlight — pulse matched tiles before destruction
      const highlightDuration = isFirstRound ? HIGHLIGHT_DURATION_FIRST : HIGHLIGHT_DURATION_CASCADE;
      await this.animateMatchHighlight(step.removedPositions, highlightDuration);

      // PHASE 2+3: Fire projectiles + destroy tiles, combo announce at first impact
      await this.animateMatchAndFireProjectiles(step, chainDepth, (impactColor) => {
        this.onRoundImpact(turnResult, stepIdx, chainDepth, roundWeight, impactColor);
      });

      // Impact settle pause — player reads the combo result
      await this.delay(IMPACT_SETTLE_MS);

      // PHASE 4: Gravity — tiles rise + new tiles spawn
      await this.animateGravityAndRefill(step.boardAfterGravity);

      // Gravity settle
      await this.delay(GRAVITY_SETTLE_MS);

      // PHASE 5: Inter-round pause (skip after final round)
      if (!isLastRound) {
        await this.delay(INTER_ROUND_PAUSE_MS);
      }
    }

    // Play overload effect if triggered
    if (turnResult.overloadEvent?.triggered) {
      playOverloadGlitch(this);
    }

    // Play skill effects
    for (const resolution of turnResult.matchResolutions) {
      if (resolution.skillTriggered) {
        const enemyPos = this.enemySprite
          ? { x: this.enemySprite.x, y: this.enemySprite.y }
          : { x: GAME_WIDTH / 2, y: 55 };
        playSkillFlash(this, enemyPos.x, enemyPos.y);
      }
    }
  }

  /** Animate match highlight: pulse matched tiles before destruction */
  private async animateMatchHighlight(
    positions: TilePosition[],
    duration: number,
  ): Promise<void> {
    const tweens: Promise<void>[] = [];

    for (const pos of positions) {
      const sprite = this.tileSprites[pos.x]?.[pos.y];
      if (!sprite) continue;

      // Scale pulse on the sprite container
      tweens.push(new Promise<void>((resolve) => {
        this.tweens.add({
          targets: sprite,
          scaleX: 1.25,
          scaleY: 1.25,
          duration: duration / 2,
          ease: 'Sine.easeOut',
          yoyo: true,
          onComplete: () => resolve(),
        });
      }));

      // Show golden match highlight ring
      sprite.setMatchHighlighted(true);

      // Spawn glow ring effect
      const worldPos = this.boardToWorld(pos.x, pos.y);
      spawnMatchGlowRing(this, worldPos.x, worldPos.y, sprite.getColor(), duration);
    }

    await Promise.all(tweens);

    // Clear highlights after tween
    for (const pos of positions) {
      this.tileSprites[pos.x]?.[pos.y]?.setMatchHighlighted(false);
    }
  }

  /** Animate matched tiles firing projectiles toward enemy, then destroy tiles */
  private async animateMatchAndFireProjectiles(
    step: CascadeStep,
    chainDepth: number,
    onFirstImpact: (impactColor: number) => void,
  ): Promise<void> {
    const enemyX = this.enemySprite?.x ?? GAME_WIDTH / 2;
    const enemyY = this.enemySprite?.y ?? 55;

    // Collect tile info before destroying
    const tileData: { worldPos: { x: number; y: number }; element: ElementType; sprite: TileSprite }[] = [];

    for (const pos of step.removedPositions) {
      const sprite = this.tileSprites[pos.x]?.[pos.y];
      if (sprite) {
        const worldPos = this.boardToWorld(pos.x, pos.y);
        tileData.push({ worldPos, element: sprite.element, sprite });
        this.tileSprites[pos.x]![pos.y] = null;
      }
    }

    // Spawn small particle burst at each tile position
    for (const data of tileData) {
      spawnMatchBurst(this, data.worldPos.x, data.worldPos.y, data.element);
    }

    // Fire projectiles from tiles to enemy (staggered 25ms between)
    const projectilePromises: Promise<{ element: ElementType; color: number }>[] = [];
    for (let i = 0; i < tileData.length; i++) {
      const data = tileData[i]!;
      const delay = i * PROJECTILE_STAGGER_MS;
      projectilePromises.push(
        spawnProjectile(
          this,
          data.worldPos.x,
          data.worldPos.y,
          enemyX,
          enemyY,
          data.element,
          delay,
        ),
      );
    }

    // Destroy tile sprites while projectiles are in flight
    const destroyPromises = tileData.map((data) => data.sprite.animateDestroy());

    // Wait for first projectile to hit before triggering hit animation + combo event
    const firstHit = projectilePromises[0];
    if (firstHit) {
      firstHit.then((result) => {
        this.enemySprite?.playImpactEffect(result.color);
        this.enemySprite?.playHitAnimation(chainDepth);
        spawnImpactBurst(this, enemyX, enemyY, result.color);
        onFirstImpact(result.color);
      });
    }

    // Wait for all projectiles and destroys to complete
    await Promise.all([...projectilePromises, ...destroyPromises]);
  }

  /** Fire all per-round feedback at the moment of first projectile impact */
  private onRoundImpact(
    turnResult: BattleTurnResult,
    stepIdx: number,
    chainDepth: number,
    roundWeight: number,
    _impactColor: number,
  ): void {
    const weightTier = Math.min(3, Math.max(0, Math.floor((roundWeight - 3) / 3)));
    const comboMultiplier = getComboMultiplier(chainDepth, roundWeight);

    // Sum damage for this cascade step's resolutions (matchResolutions is a flat array)
    let offset = 0;
    for (let s = 0; s < stepIdx; s++) {
      offset += turnResult.cascadeSteps[s]!.matches.length;
    }
    const stepMatchCount = turnResult.cascadeSteps[stepIdx]!.matches.length;
    const stepResolutions = turnResult.matchResolutions.slice(offset, offset + stepMatchCount);
    const roundDamage = stepResolutions.reduce((sum, r) => sum + r.damage.total, 0);
    const isCrit = stepResolutions.some((r) => r.damage.isCrit);

    // Emit per-round combo event for React UI
    EventBus.emit('cascadeRoundComplete', {
      chainDepth,
      roundWeight,
      weightTier,
      comboMultiplier,
      roundDamage,
      isCrit,
    });

    // Spawn damage number with chain-depth-based vertical offset (stacking)
    const enemyX = this.enemySprite?.x ?? GAME_WIDTH / 2;
    const enemyY = this.enemySprite?.y ?? 55;
    spawnDamageNumber(this, {
      x: enemyX + (Math.random() - 0.5) * 40,
      y: enemyY - 20 - (stepIdx * 12),
      amount: roundDamage,
      isCrit,
    });

    // Spawn combo flash for chainDepth >= 2
    if (chainDepth >= 2) {
      spawnComboFlash(this, chainDepth);
    }

    // 增量更新 HP 条 — 计算截至当前 step 的累计伤害
    if (this.enemySprite) {
      const enemy = useBattleStore.getState().enemy;
      if (enemy) {
        let cumulativeDamage = 0;
        let resOffset = 0;
        for (let s = 0; s <= stepIdx; s++) {
          const stepMatchCount = turnResult.cascadeSteps[s]!.matches.length;
          const stepRes = turnResult.matchResolutions.slice(resOffset, resOffset + stepMatchCount);
          cumulativeDamage += stepRes.reduce((sum, r) => sum + r.damage.total, 0);
          resOffset += stepMatchCount;
        }
        const hpBeforeTurn = enemy.hp + turnResult.totalDamage;
        const intermediateHp = Math.max(enemy.hp, hpBeforeTurn - cumulativeDamage);
        this.enemySprite.setHp(intermediateHp);
      }
    }
  }

  /** Animate gravity: sync sprites with updated board state, animate moves */
  private async animateGravityAndRefill(targetTiles: (Tile | null)[][]): Promise<void> {
    const tiles = targetTiles;
    const fallPromises: Promise<void>[] = [];

    // Step 1: Collect all surviving sprites into a flat map keyed by their tileX, tileY
    // (where they THINK they are, based on their stored tileX/tileY)
    const survivingSprites = new Map<string, TileSprite>();
    for (let x = 0; x < BOARD_COLS; x++) {
      for (let y = 0; y < BOARD_ROWS; y++) {
        const sprite = this.tileSprites[x]?.[y];
        if (sprite && !sprite.scene) {
          // Sprite was destroyed, skip
          continue;
        }
        if (sprite) {
          // Key by sprite's stored board position
          survivingSprites.set(`${sprite.tileX},${sprite.tileY}`, sprite);
        }
      }
    }

    // Step 2: Build a new sprite grid that matches the board state
    const newTileSprites: (TileSprite | null)[][] = [];
    for (let x = 0; x < BOARD_COLS; x++) {
      newTileSprites[x] = [];
      for (let y = 0; y < BOARD_ROWS; y++) {
        newTileSprites[x]![y] = null;
      }
    }

    // Step 3: For each board tile, find or create the matching sprite
    for (let x = 0; x < BOARD_COLS; x++) {
      for (let y = 0; y < BOARD_ROWS; y++) {
        const boardTile = tiles[x]?.[y];
        if (!boardTile) continue;

        const targetPos = this.boardToWorld(x, y);

        // Try to find a surviving sprite that matches this tile's element
        // by looking for sprites in the same column that could have moved here
        let foundSprite: TileSprite | null = null;

        // Look for sprites in the same column that match the element
        for (let oldY = y; oldY < BOARD_ROWS; oldY++) {
          const key = `${x},${oldY}`;
          const sprite = survivingSprites.get(key);
          if (sprite && sprite.element === boardTile.element) {
            foundSprite = sprite;
            survivingSprites.delete(key);
            break;
          }
        }

        if (foundSprite) {
          // Update sprite's board position and animate to new location
          foundSprite.tileX = x;
          foundSprite.tileY = y;

          // Re-register click handler with correct position
          foundSprite.off('pointerdown');
          foundSprite.on('pointerdown', () => this.onTileClick(x, y));

          newTileSprites[x]![y] = foundSprite;

          // Animate if position changed
          if (Math.abs(foundSprite.y - targetPos.y) > 2) {
            fallPromises.push(foundSprite.animateRise(targetPos.y));
          }

          // Update locked state
          foundSprite.setLocked(!!boardTile.locked);
        } else {
          // New tile — spawn from below (player side) and float up
          const startY = this.gridOriginY + (BOARD_ROWS + 1) * TILE_SIZE;

          const sprite = new TileSprite(this, targetPos.x, startY, boardTile.element, x, y);
          sprite.on('pointerdown', () => this.onTileClick(x, y));
          if (boardTile.locked) {
            sprite.setLocked(true);
          }
          newTileSprites[x]![y] = sprite;

          // Delay based on distance from bottom (y=4 spawns first, y=0 last)
          const distFromBottom = BOARD_ROWS - 1 - y;
          fallPromises.push(sprite.animateRise(targetPos.y, distFromBottom * 30));
        }
      }
    }

    // Step 4: Clean up any orphaned sprites (shouldn't happen but safety)
    for (const sprite of survivingSprites.values()) {
      if (sprite.scene) {
        sprite.destroy();
      }
    }

    // Step 5: Replace the sprite grid
    this.tileSprites = newTileSprites;

    await Promise.all(fallPromises);
  }

  // ──────────────────────────────────────────────────────
  // Enemy visual updates
  // ──────────────────────────────────────────────────────

  /** 同步 sprite 与 boardStore 的差异（处理技能 BOARD_CHANGE 等非 cascade 变化） */
  private syncBoardVisual(): void {
    const { tiles } = useBoardStore.getState();

    for (let x = 0; x < BOARD_COLS; x++) {
      for (let y = 0; y < BOARD_ROWS; y++) {
        const boardTile = tiles[x]?.[y];
        const sprite = this.tileSprites[x]?.[y];

        if (!boardTile && sprite) {
          sprite.destroy();
          this.tileSprites[x]![y] = null;
        } else if (boardTile && !sprite) {
          const pos = this.boardToWorld(x, y);
          const newSprite = new TileSprite(this, pos.x, pos.y, boardTile.element, x, y);
          newSprite.on('pointerdown', () => this.onTileClick(x, y));
          if (boardTile.locked) newSprite.setLocked(true);
          this.tileSprites[x]![y] = newSprite;
        } else if (boardTile && sprite && sprite.element !== boardTile.element) {
          sprite.setElement(boardTile.element);
        }
      }
    }
  }

  private updateEnemyVisual(_turnResult: BattleTurnResult): void {
    if (!this.enemySprite) return;

    const enemy = useBattleStore.getState().enemy;
    if (!enemy) return;

    this.enemySprite.setHp(enemy.hp);
    this.enemySprite.setDemandChange(enemy.phase);
  }

  // ──────────────────────────────────────────────────────
  // Full board rebuild (for reshuffle or board changes)
  // ──────────────────────────────────────────────────────

  /** Destroy all tile sprites and rebuild from board state */
  rebuildBoard(): void {
    // Destroy existing sprites
    for (let x = 0; x < BOARD_COLS; x++) {
      for (let y = 0; y < BOARD_ROWS; y++) {
        const sprite = this.tileSprites[x]?.[y];
        if (sprite) {
          sprite.destroy();
        }
      }
    }
    this.tileSprites = [];
    this.buildBoard();
  }

  // ──────────────────────────────────────────────────────
  // EventBus integration
  // ──────────────────────────────────────────────────────

  private handlePlayerSwap = (data: { from: { x: number; y: number }; to: { x: number; y: number } }): void => {
    if (this.animating) return;
    this.executeSwap(data.from, data.to);
  };

  private handleRestartBattle = (): void => {
    this.rebuildBoard();
  };

  private registerEventListeners(): void {
    EventBus.on('playerSwap', this.handlePlayerSwap);
    EventBus.on('restartBattle', this.handleRestartBattle);
  }

  private cleanupEventListeners(): void {
    EventBus.off('playerSwap', this.handlePlayerSwap);
    EventBus.off('restartBattle', this.handleRestartBattle);
  }

  // ──────────────────────────────────────────────────────
  // Utility
  // ──────────────────────────────────────────────────────

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.time.delayedCall(ms, resolve);
    });
  }
}
